import { ICON_NAMES, type IconName, isIconName } from './iconLoaders';

type AppIconId = IconName;

export const APP_ICON_IDS: readonly AppIconId[] = ICON_NAMES;

export interface IconStyle {
  size: string;
  color: string;
  alignment: string;
  margin: string;
  padding: string;
}

export const DEFAULT_ICON_STYLE: IconStyle = {
  size: 'medium',
  color: 'primary',
  alignment: 'inline',
  margin: 'medium',
  padding: 'none',
};

const ICON_COLOR_SX: Record<string, string> = {
  primary: 'primary.main',
  secondary: 'secondary.main',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
  light: 'grey.300',
  dark: 'grey.800',
};

export function iconStyleColorToSx(color: string): string {
  return ICON_COLOR_SX[color] ?? 'primary.main';
}

export function iconStyleSizeToFontSize(size: string): string {
  switch (size) {
    case 'small':
      return '0.875rem';
    case 'large':
      return '1.25rem';
    case 'xl':
      return '1.5rem';
    case 'xxl':
      return '2rem';
    default:
      return '1rem';
  }
}

const ICON_SIZE_CLASS = /^icon-(small|medium|large|xl|xxl)$/;
const ICON_COLOR_CLASS =
  /^icon-(primary|secondary|success|warning|error|info|light|dark)$/;

const ICON_COLOR_HEX: Record<string, string> = {
  primary: '#9C5A37',
  secondary: '#404D44',
  success: '#74C69D',
  warning: '#E09F3E',
  error: '#C84630',
  info: '#B9E6FF',
  light: '#EFEAD8',
  dark: '#1A2421',
};

/** Resolve TinyMCE icon class tokens to concrete size/color for MUI SvgIcon. */
export function parseIconVisualFromClass(className: string): { fontSize: string; color: string } {
  const tokens = className.split(/\s+/).filter(Boolean);
  const sizeToken = tokens.find(token => ICON_SIZE_CLASS.test(token));
  const colorToken = tokens.find(token => ICON_COLOR_CLASS.test(token));
  const sizeKey = sizeToken?.replace('icon-', '') ?? 'medium';
  const colorKey = colorToken?.replace('icon-', '') ?? 'primary';

  return {
    fontSize: iconStyleSizeToFontSize(sizeKey),
    color: ICON_COLOR_HEX[colorKey] ?? ICON_COLOR_HEX.primary ?? '#9C5A37',
  };
}

export function buildIconStyleClassString(style: IconStyle): string {
  const classes = ['icon', `icon-${style.size}`, `icon-${style.color}`, `icon-${style.alignment}`];

  if (style.margin !== 'none') {
    classes.push(`icon-margin-${style.margin}`);
  }
  if (style.padding !== 'none') {
    classes.push(`icon-padding-${style.padding}`);
  }

  return classes.join(' ');
}

/** Larger preview sizes for icon picker grids (not published content defaults). */
export function iconStyleSizeToGridFontSize(size: string): string {
  switch (size) {
    case 'small':
      return '1.35rem';
    case 'large':
      return '2rem';
    case 'xl':
      return '2.35rem';
    case 'xxl':
      return '2.75rem';
    default:
      return '1.75rem';
  }
}

/** HTML for TinyMCE / page content (custom `<icon>` element). */
export function buildIconHtml(iconId: string, style: IconStyle = DEFAULT_ICON_STYLE): string {
  if (!iconId) return '';
  return `<icon class="${buildIconStyleClassString(style)}" icon="${iconId}"></icon>`;
}

export function isAppIconId(value: string): value is AppIconId {
  return isIconName(value);
}
